import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ElementContainer,
  FlexibleContainer,
  HorizontalLine,
  RowContainer,
} from "../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../components/common/styledComponents";
import {
  Icon,
  IconContainer,
} from "../../../components/visuals/styledComponents";
import { MilestoneKey } from "../../../enums/Status";
import { MILESTONE_TYPES } from "../../CompanyProfile/constants";
import { formatDateYMD } from "../../../utils/formattingUtils";
import {
  engagementBannerColors,
  graphPaletteV2,
  greyText,
  lightGreys,
} from "../../../styles";
import {
  EngagementCategory,
  EngagementRecord,
  EngagementType,
} from "../../../models/stewardshipModels";
import { findMilestoneIndex } from "../utils";
import { ROUTES } from "../../../routes/routeNames";
import { FlagKey } from "../../../models/workflowModels";
import { getFlagData } from "../../../components/visuals/FlagBox";
import { Avatar } from "../../../components/visuals/Avatar";
import { ToggleAccordionBlueText } from "../../../components/buttons/ToggleAccordionBlueText";
import { EscalationParentType } from "../shared/EscalationAccordion";
import { CompassProgressBar } from "../../../components/common/CompassProgressBar";
import styled from "styled-components";
import { useEngagementVotingMapping } from "../../../hooks/useEngagementVotingMapping";
import { EngagementVotingBadgeList } from "../../../components/visuals/EngagementVotingBadge";

export type EngagementBannerType = {
  recordId: string;
  theme: EngagementType;
  subTheme: string;
  manager: string;
  managerImage: string | null;
  managerId: string;
  title: string;
  milestone: MilestoneKey;
  milestoneStart: string; // ISO date string
  endDate: string | null; // ISO date string
  flagStatus: FlagKey;
  category: EngagementCategory;
  escalationReached: EscalationParentType | null;
  // Full record for navigation state
  record: EngagementRecord;
};

const EngagementBannerContainer = styled(FlexibleContainer)`
  min-width: 300px;
  border: 1px solid ${lightGreys.borderLightGrey};
`;

const InfoRow = styled(RowContainer)`
  margin-top: 6px;
  margin-bottom: 6px;
  justify-content: flex-start;
`;

const EngagementBanner: React.FC<{
  engagement: EngagementBannerType;
}> = ({ engagement }) => {
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const navigate = useNavigate();
  const flagData = getFlagData(engagement.flagStatus as FlagKey);
  const milestoneProgress = findMilestoneIndex(
    engagement.category,
    engagement.milestone
  );

  const handleNavigateToEngagement = (): void => {
    navigate(`${ROUTES.SINGLE_ENGAGEMENT}/${engagement.recordId}`, {
      state: { engagementRecord: engagement.record },
    });
  };

  // Fetch voting linkages for this engagement
  const { getMappingsForEngagement } = useEngagementVotingMapping({
    engagementRecordId: engagement.recordId,
  });

  const votingMappings = getMappingsForEngagement(engagement.recordId).map(
    (mapping) => ({
      votingRecordId: mapping.voting_record_id,
      confidenceScore: mapping.confidence_score,
      interactionClass: mapping.interaction_class,
      linkageType: mapping.linkage_type,
      sequenceFlow: mapping.sequence_flow,
      matchingFactors: mapping.matching_factors,
      rationale: mapping.rationale,
    })
  );

  return (
    <EngagementBannerContainer
      ml="10px"
      w="300px"
      jc="flex-start"
      ai="flex-start"
      p="10px 16px"
      br="16px"
      bg="white"
      fd="column"
      fw="wrap"
      mb="10px"
    >
      {/* Main Card Details */}
      {/* Engagement title */}
      <RowContainer ai="center" jc="space-between" mt={1} mb={4}>
        <CompassParagraph
          hoverUnderline={true}
          cursor="pointer"
          fontSize={14}
          style={{ fontWeight: 600 }}
          onClick={handleNavigateToEngagement}
        >
          {engagement.title}
        </CompassParagraph>
        <Avatar
          username={engagement.manager}
          image={engagement.managerImage}
          tooltip={true}
          size="large"
        />
      </RowContainer>
      {/* Engagement Milestone bar */}
      <RowContainer mt={1} mb={4}>
        <CompassParagraph fontSize={12} color={greyText}>
          Milestone: {MILESTONE_TYPES[milestoneProgress].displayValue}
        </CompassParagraph>
      </RowContainer>
      <RowContainer mt={1}>
        <CompassProgressBar
          value={milestoneProgress + 1}
          max={4}
          color={graphPaletteV2[milestoneProgress]}
        />
      </RowContainer>
      <RowContainer mt={6}>
        <ToggleAccordionBlueText
          handleChange={setIsCardExpanded}
          isOpen={isCardExpanded}
        />
      </RowContainer>
      {/* Expanded Card Details */}
      {isCardExpanded && (
        <>
          {/* Engagement Type */}
          <InfoRow>
            <CompassParagraph fontSize={12}>Type:</CompassParagraph>
            <CompassParagraph fontSize={11} color={greyText}>
              {engagement.category}
            </CompassParagraph>
          </InfoRow>
          {/* Engagement Dates */}
          <InfoRow>
            <CompassParagraph fontSize={12}>Dates:</CompassParagraph>
            <CompassParagraph fontSize={11} color={greyText}>
              {formatDateYMD(engagement.milestoneStart)} -{" "}
              {formatDateYMD(engagement.endDate) || "Ongoing"}
            </CompassParagraph>
          </InfoRow>
          {/* Workflow status */}
          <InfoRow>
            <ElementContainer ai="center">
              <CompassParagraph fontSize={12}>
                Workflow status:
              </CompassParagraph>
              <IconContainer size={14} p={-1} m={3}>
                <Icon src={flagData.icon} />
              </IconContainer>
              <CompassParagraph fontSize={11} color={greyText} mr={-1}>
                {flagData.text}
              </CompassParagraph>
            </ElementContainer>
          </InfoRow>
          {/* Escalation Reached */}
          <HorizontalLine marginTop={4} marginBottom={8} />
          <RowContainer mb={4} mt={6}>
            <CompassParagraph fontSize={12} color={greyText}>
              Escalation:{" "}
              {engagement.escalationReached?.title || "Escalation not reached"}
            </CompassParagraph>
          </RowContainer>
          <RowContainer mt={1}>
            <CompassProgressBar
              value={engagement.escalationReached?.key || 0}
              max={5}
              color={engagement.escalationReached?.color}
            />
          </RowContainer>

          {/* Voting Linkages Section */}
          {votingMappings.length > 0 && (
            <>
              <HorizontalLine marginTop={8} marginBottom={8} />
              <RowContainer mb={4} mt={6}>
                <CompassParagraph fontSize={12} bold={true}>
                  Voting Linkages ({votingMappings.length})
                </CompassParagraph>
              </RowContainer>
              <RowContainer mt={1}>
                <EngagementVotingBadgeList mappings={votingMappings} />
              </RowContainer>
            </>
          )}
        </>
      )}
    </EngagementBannerContainer>
  );
};

export const EngagementBannerList: React.FC<{
  engagements: EngagementBannerType[];
}> = ({ engagements }) => {
  const sortedEngagements = engagements.sort(
    (a, b) =>
      new Date(a.milestoneStart).getTime() -
      new Date(b.milestoneStart).getTime()
  );

  return (
    <RowContainer jc="flex-start" ai="flex-start" mb={10} wrap="wrap">
      {sortedEngagements.map((engagement, index) => (
        <EngagementBanner key={index} engagement={engagement} />
      ))}
    </RowContainer>
  );
};

export interface Manager {
  managerId: string;
  manager: string;
}

export const getManagerBannerColor = (
  managerId: string,
  managers: Manager[]
): { background: string; border: string } => {
  if (!managers.length) {
    return { background: "#ccc", border: "#999" };
  }

  const index = managers.findIndex((mgr) => mgr.managerId === managerId);

  const managerIndex = index !== -1 ? index : 0;

  const color =
    engagementBannerColors[managerIndex % engagementBannerColors.length];

  return {
    background: "white",
    border: color.text,
  };
};
